'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import DefaultEmptyImage from './images/default-empty.png';
import type { EmptyProps } from './interface';

export type { EmptyProps };

/**
 * Empty 空状态组件
 * 
 * 用于展示无数据的空状态，支持自定义图片、提示文本和样式
 * 
 * @example
 * // 使用默认配置
 * <Empty />
 * 
 * @example
 * // 自定义提示文本和样式
 * <Empty tip="暂无数据" tipClassName="text-red-500" />
 * 
 * @example
 * // 自定义图片和尺寸
 * <Empty 
 *   image={CustomImage} 
 *   imageSize={{ width: 100, height: 100 }}
 * />
 */
export default function Empty(props: EmptyProps) {
  const {
    className = '',
    image = DefaultEmptyImage,
    imageSize = { width: 52, height: 29 },
    tip,
    tipClassName = ''
  } = props;

  const t = useTranslations('common');

  // 如果用户没有传递 tip，则使用默认的多语言文本
  const displayTip = tip ?? t('noRecords');

  // 处理图片源：StaticImageData 需要使用 .src，字符串直接使用
  const imageSrc = typeof image === 'string' ? image : image.src;

  const { width = 52, height = 29 } = imageSize;

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <Image
        src={imageSrc}
        alt="empty"
        width={width}
        height={height}
      />
      {displayTip && (
        <div className={`text-tertiary text-sm font-medium mt-4 ${tipClassName}`}>
          {displayTip}
        </div>
      )}
    </div>
  );
}

